/**
 * QuestTypeformUserResponse
 * Enrollment form answers for one participant — v7 response modal layout.
 */
import React, { useState, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import { ABSpinner, FeatherLoader, useErrorService } from '@abroad/components'
import API from '../../utils/API'
import { formatExperienceDate } from '../../utils/date'

const stripHtml = (html) => {
  if (!html) return ''
  return String(html)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const ViewIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'>
    <path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z' />
    <circle cx='12' cy='12' r='3' />
  </svg>
)

const DownloadIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'>
    <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
    <polyline points='7 10 12 15 17 10' />
    <line x1='12' y1='15' x2='12' y2='3' />
  </svg>
)

const QuestTypeformUserResponse = ({
  userId,
  userName,
  userEmail,
  submittedAt,
  onBack,
  customFormId,
  questId,
  showModal = true,
}) => {
  const [userResponse, setUserResponse] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [fileUrls, setFileUrls] = useState({})
  const [fileLoadingStates, setFileLoadingStates] = useState({})
  const [docPreview, setDocPreview] = useState({
    show: false,
    title: '',
    url: '',
    fileName: '',
    isImage: false,
    mediaReady: false,
  })
  const Error = useErrorService()

  const isImageFile = (filename) => {
    if (!filename) return false
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp']
    const lowerFilename = String(filename).toLowerCase()
    return imageExtensions.some((ext) => lowerFilename.includes(ext))
  }

  const isPdfFile = (filename) => {
    if (!filename) return false
    return String(filename).toLowerCase().includes('.pdf')
  }

  const shouldShowDependentQuestion = (question, allAnswers) => {
    if (question.questionType !== 'dependent') {
      return true
    }

    const parentQuestion = allAnswers.find(
      (answer) => answer.questionId === question.dependentQuestionId,
    )
    if (!parentQuestion) {
      return false
    }

    let parentAnswer = parentQuestion.answer
    if (!parentAnswer) {
      return false
    }

    const conditions = question.condition || []
    if (conditions.length === 0) {
      return false
    }

    if (parentQuestion.answerType === 'multipleSelectionDropdown') {
      if (Array.isArray(parentAnswer)) {
        return parentAnswer.some((selectedOption) => {
          const selectedOptionObj = parentQuestion.options?.find(
            (opt) => opt.option === selectedOption,
          )
          const selectedOptionId =
            selectedOptionObj?._id || selectedOptionObj?.id
          return conditions.some((condition) => selectedOptionId === condition)
        })
      }
      return false
    }

    const answerValue = Array.isArray(parentAnswer)
      ? parentAnswer[0]
      : parentAnswer
    const selectedOptionObj = parentQuestion.options?.find(
      (opt) => opt.option === answerValue,
    )
    const selectedOptionId = selectedOptionObj?._id || selectedOptionObj?.id
    return conditions.some((condition) => selectedOptionId === condition)
  }

  const getFilteredAnswers = (answers) => {
    if (!answers) return answers
    return answers.filter((answer) =>
      shouldShowDependentQuestion(answer, answers),
    )
  }

  useEffect(() => {
    const fetchUserResponse = async () => {
      if (!questId || !userId || !customFormId) return

      setIsLoading(true)
      setFileUrls({})
      setFileLoadingStates({})
      try {
        const { data } = await API.ClientAPI.quests.getCustomFormAnswersOfUser(
          questId,
          userId,
          customFormId,
        )
        setUserResponse(data)
        setIsLoading(false)
      } catch (e) {
        Error.showError(e)
        setIsLoading(false)
      }
    }

    fetchUserResponse()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questId, userId, customFormId])

  useEffect(() => {
    const preloadFileUrls = async () => {
      if (!userResponse?.answers) return

      try {
        const fileAnswers =
          userResponse.answers?.filter((answer) => {
            const answerValue = Array.isArray(answer.answer)
              ? answer.answer[0]
              : answer.answer
            return isImageFile(answerValue) || isPdfFile(answerValue)
          }) || []

        fileAnswers.forEach((answer) => {
          const answerValue = Array.isArray(answer.answer)
            ? answer.answer[0]
            : answer.answer
          setFileLoadingStates((prev) => ({ ...prev, [answerValue]: true }))
        })

        const urlResults = await Promise.all(
          fileAnswers.map(async (answer) => {
            const answerValue = Array.isArray(answer.answer)
              ? answer.answer[0]
              : answer.answer
            try {
              const { data } =
                await API.ClientAPI.quests.downloadTravelDocument(
                  questId,
                  userId,
                  answerValue,
                )
              return { fileName: answerValue, url: data?.url }
            } catch (error) {
              Error.showError(error)
              return { fileName: answerValue, url: null }
            }
          }),
        )

        urlResults.forEach(({ fileName, url }) => {
          if (url) {
            setFileUrls((prev) => ({ ...prev, [fileName]: url }))
          }
          setFileLoadingStates((prev) => ({ ...prev, [fileName]: false }))
        })
      } catch (error) {
        Error.showError(error)
      }
    }

    preloadFileUrls()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userResponse, questId, userId])

  const openFile = (fileName, questionTitle) => {
    const fileUrl = fileUrls[fileName]
    if (!fileUrl) return
    if (isImageFile(fileName) || isPdfFile(fileName)) {
      setDocPreview({
        show: true,
        title: stripHtml(questionTitle) || 'Document',
        url: fileUrl,
        fileName,
        isImage: isImageFile(fileName),
        mediaReady: false,
      })
      return
    }
    window.open(fileUrl, '_blank')
  }

  const downloadFile = (fileName) => {
    const fileUrl = fileUrls[fileName]
    if (!fileUrl) return
    const link = document.createElement('a')
    link.href = fileUrl
    link.download = fileName
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const closeDocPreview = () => {
    setDocPreview({
      show: false,
      title: '',
      url: '',
      fileName: '',
      isImage: false,
      mediaReady: false,
    })
  }

  const markDocMediaReady = () => {
    setDocPreview((prev) => (prev.show ? { ...prev, mediaReady: true } : prev))
  }

  const formatAnswerValue = (answer, value) => {
    const text = value == null ? '' : String(value).trim()
    if (!text) return ''
    if (answer?.answerType === 'date') {
      return formatExperienceDate(text) || text
    }
    return text
  }

  const renderFileActs = (fileName, questionTitle) => {
    const isFileLoading = fileLoadingStates[fileName]
    const fileUrl = fileUrls[fileName]

    if (isFileLoading) {
      return <span className='experience-inquiry-empty'>Loading…</span>
    }
    if (!fileUrl) {
      return <span className='experience-inquiry-empty'>Not uploaded</span>
    }

    return (
      <span className='experience-response-doc-acts'>
        <button
          type='button'
          className='experience-response-doc-ico'
          aria-label='View'
          onClick={() => openFile(fileName, questionTitle)}>
          <ViewIcon />
        </button>
        <button
          type='button'
          className='experience-response-doc-ico'
          aria-label='Download'
          onClick={() => downloadFile(fileName)}>
          <DownloadIcon />
        </button>
      </span>
    )
  }

  const renderAnswerContent = (answer) => {
    if (!answer) {
      return (
        <span className='experience-inquiry-empty'>No answer provided</span>
      )
    }

    if (answer.subAnswers) {
      const lines = answer.subAnswers
        .map((subAnswer) => {
          const val = subAnswer.answer?.[0]
          return val && String(val).trim() ? String(val).trim() : null
        })
        .filter(Boolean)

      if (lines.length === 0) {
        return (
          <span className='experience-inquiry-empty'>No answer provided</span>
        )
      }

      return <div className='experience-inquiry-answer'>{lines.join('\n')}</div>
    }

    if (!answer.answer) {
      return (
        <span className='experience-inquiry-empty'>No answer provided</span>
      )
    }

    if (Array.isArray(answer.answer)) {
      if (answer.answer.length === 0) {
        return (
          <span className='experience-inquiry-empty'>No answer provided</span>
        )
      }

      const nonEmptyAnswers = answer.answer.filter(
        (ans) => ans && String(ans).trim().length > 0,
      )

      if (nonEmptyAnswers.length === 0) {
        return (
          <span className='experience-inquiry-empty'>No answer provided</span>
        )
      }

      const answerValue = nonEmptyAnswers[0]

      if (isImageFile(answerValue) || isPdfFile(answerValue)) {
        return renderFileActs(answerValue, answer.question)
      }

      return (
        <div className='experience-inquiry-answer'>
          {nonEmptyAnswers
            .map((value) => formatAnswerValue(answer, value))
            .join(', ')}
        </div>
      )
    }

    const text = String(answer.answer).trim()
    if (!text) {
      return (
        <span className='experience-inquiry-empty'>No answer provided</span>
      )
    }

    if (isImageFile(text) || isPdfFile(text)) {
      return renderFileActs(text, answer.question)
    }

    return (
      <div className='experience-inquiry-answer'>
        {formatAnswerValue(answer, text)}
      </div>
    )
  }

  const email =
    userEmail || userResponse?.email || userResponse?.user?.email || ''
  const answeredAt =
    submittedAt ||
    userResponse?.answeredAt ||
    userResponse?.submittedAt ||
    userResponse?.createdAt
  const leadParts = [
    email,
    answeredAt ? `submitted ${formatExperienceDate(answeredAt)}` : null,
  ].filter(Boolean)

  const body = (
    <div className='experience-response-modal'>
      <div className='experience-response-head'>
        <h2>{userName || 'Enrollment form'}</h2>
        <span className='badge-experience badge-live'>Submitted</span>
      </div>
      {leadParts.length > 0 ? (
        <p className='experience-response-lead'>{leadParts.join(' · ')}</p>
      ) : (
        <p className='experience-response-lead'>Enrollment form responses</p>
      )}

      <div className='experience-response-qa'>
        {isLoading ? (
          <div className='text-center py-4'>
            <ABSpinner />
          </div>
        ) : !userResponse?.answers || userResponse.answers.length === 0 ? (
          <p className='experience-inquiry-empty'>
            No responses found for this user.
          </p>
        ) : (
          getFilteredAnswers(userResponse.answers).map((answer, index) => (
            <div className='experience-response-qa-item' key={index}>
              <h4
                className='question-html-content'
                dangerouslySetInnerHTML={{
                  __html: answer.question || 'Question',
                }}
              />
              {answer.description ? (
                <p className='experience-inquiry-prompt'>
                  {stripHtml(answer.description)}
                </p>
              ) : null}
              {renderAnswerContent(answer)}
            </div>
          ))
        )}
      </div>

      <div className='experience-modal-actions'>
        <button
          type='button'
          className='btn-outline-experience'
          onClick={onBack}>
          Close
        </button>
      </div>
    </div>
  )

  return (
    <>
      {showModal ? (
        <Modal
          show
          onHide={onBack}
          centered
          enforceFocus={false}
          dialogClassName='experience-response-dialog'
          contentClassName='experience-response-card'>
          <Modal.Body className='p-0'>{body}</Modal.Body>
        </Modal>
      ) : (
        body
      )}

      <Modal
        show={docPreview.show}
        onHide={closeDocPreview}
        centered
        enforceFocus={false}
        className='experience-nested-doc-modal'
        backdropClassName='experience-nested-doc-backdrop'
        dialogClassName='experience-doc-dialog'
        contentClassName='experience-doc-card'>
        <Modal.Body className='p-0'>
          <div className='experience-doc-modal experience-doc-modal-lg'>
            <h2>{docPreview.title || 'Document'}</h2>
            <p className='experience-doc-lead'>{userName}</p>
            <div className='experience-doc-preview'>
              <div
                className={`experience-doc-preview-frame${
                  /headshot/i.test(docPreview.title) ? ' is-headshot' : ''
                }${docPreview.mediaReady ? '' : ' is-loading'}`}>
                {!docPreview.mediaReady ? (
                  <div className='d-flex justify-content-center align-items-center h-100 w-100 position-absolute'>
                    <FeatherLoader width={48} />
                  </div>
                ) : null}
                {docPreview.isImage ? (
                  <img
                    src={docPreview.url}
                    alt={docPreview.fileName}
                    style={{
                      display: docPreview.mediaReady ? 'block' : 'none',
                    }}
                    onLoad={markDocMediaReady}
                    onError={markDocMediaReady}
                  />
                ) : (
                  <iframe
                    title={docPreview.fileName || 'Document'}
                    src={docPreview.url}
                    style={{
                      display: docPreview.mediaReady ? 'block' : 'none',
                    }}
                    onLoad={markDocMediaReady}
                    onError={markDocMediaReady}
                  />
                )}
              </div>
              {docPreview.fileName ? (
                <small>{docPreview.fileName}</small>
              ) : null}
            </div>
            <div className='experience-modal-actions'>
              <button
                type='button'
                className='btn-outline-experience'
                onClick={() => downloadFile(docPreview.fileName)}>
                Download
              </button>
              <button
                type='button'
                className='btn-outline-experience'
                onClick={closeDocPreview}>
                Close
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  )
}

export default QuestTypeformUserResponse
